import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import TaskCard from "@/components/TaskCard";
import {
  Coins, Trophy, Sparkle, ClipboardText, ShieldCheck,
  TrendUp, Users, ArrowRight,
} from "@phosphor-icons/react";

function Stat({ label, value, accent, testid }) {
  return (
    <div className="border border-border p-4 bg-background" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <div className={`text-2xl sm:text-3xl mt-2 font-bold font-mono tracking-tight ${accent || ""}`}>{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [reputation, setReputation] = useState(null);
  const [overview, setOverview] = useState(null);
  const [depts, setDepts] = useState({});
  const [pendingApprovals, setPendingApprovals] = useState(0);

  useEffect(() => {
    (async () => {
      const [tasksRes, walletRes, repRes, ovRes, deptsRes] = await Promise.all([
        api.get("/tasks?limit=6&sort=newest"),
        api.get("/wallet/me"),
        api.get(`/reputation/${user.user_id}`),
        api.get("/analytics/overview"),
        api.get("/departments"),
      ]);
      setTasks(tasksRes.data);
      setWallet(walletRes.data);
      setReputation(repRes.data);
      setOverview(ovRes.data);
      setDepts(Object.fromEntries(deptsRes.data.map((d) => [d.dept_id, d.name])));
      if (["ADMIN", "MANAGER"].includes(user.role)) {
        try {
          const ap = await api.get("/approvals/pending");
          setPendingApprovals(ap.data.length);
        } catch {}
      }
    })();
  }, [user]);

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{user.role} dashboard</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-bold mt-1">Welcome back, {user.name.split(" ")[0]}.</h1>
          <p className="text-sm text-zinc-500 mt-1">Here's what's moving in the marketplace today.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-sm" onClick={() => navigate("/marketplace")} data-testid="dashboard-browse-btn">
            Browse marketplace <ArrowRight size={14} className="ml-1" />
          </Button>
          <Button className="rounded-sm bg-foreground text-background hover:bg-zinc-800 dark:hover:bg-zinc-200" onClick={() => navigate("/tasks/new")} data-testid="dashboard-create-btn">
            Post a task
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <Stat
          label="Credits Balance"
          value={wallet?.balance ?? 0}
          accent="text-[#E1252A]"
          testid="stat-credits"
        />
        <Stat label="Impact Points" value={reputation?.impact_points ?? 0} testid="stat-impact" />
        <Stat label="Tasks Completed" value={reputation?.completed_tasks ?? 0} testid="stat-completed" />
        <Stat label="Active Tasks" value={overview?.in_progress ?? 0} testid="stat-active" />
      </div>

      {/* Manager / Admin specific */}
      {["ADMIN", "MANAGER"].includes(user.role) && pendingApprovals > 0 && (
        <Card className="rounded-sm border-border bg-background" data-testid="dashboard-approvals-card">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ShieldCheck size={20} className="text-[#E1252A]" />
              <div>
                <div className="font-semibold">{pendingApprovals} approval{pendingApprovals > 1 ? "s" : ""} pending</div>
                <div className="text-xs text-zinc-500">Tasks awaiting your decision</div>
              </div>
            </div>
            <Button variant="outline" size="sm" className="rounded-sm" onClick={() => navigate("/approvals")} data-testid="dashboard-go-approvals">
              Review
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Recent tasks */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl sm:text-2xl tracking-tight font-semibold">Recent in the marketplace</h2>
          <Button variant="ghost" size="sm" className="rounded-sm" onClick={() => navigate("/marketplace")} data-testid="dashboard-view-all-btn">
            View all <ArrowRight size={14} className="ml-1" />
          </Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
          {tasks.map((t) => (
            <TaskCard key={t.task_id} task={t} departmentName={depts[t.department_id]} />
          ))}
        </div>
      </section>

      {/* Marketplace overview */}
      {overview && (
        <section className="border border-border p-6 bg-surface/40">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">Marketplace pulse</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
            <div>
              <div className="flex items-center gap-2 text-zinc-500 mb-1"><ClipboardText size={14} /><span className="text-xs uppercase tracking-wider">Total tasks</span></div>
              <div className="text-2xl font-bold font-mono">{overview.total_tasks}</div>
            </div>
            <div>
              <div className="flex items-center gap-2 text-zinc-500 mb-1"><Sparkle size={14} /><span className="text-xs uppercase tracking-wider">Open</span></div>
              <div className="text-2xl font-bold font-mono text-emerald-600">{overview.open_tasks}</div>
            </div>
            <div>
              <div className="flex items-center gap-2 text-zinc-500 mb-1"><TrendUp size={14} /><span className="text-xs uppercase tracking-wider">In motion</span></div>
              <div className="text-2xl font-bold font-mono">{overview.in_progress}</div>
            </div>
            <div>
              <div className="flex items-center gap-2 text-zinc-500 mb-1"><Coins size={14} /><span className="text-xs uppercase tracking-wider">Credits live</span></div>
              <div className="text-2xl font-bold font-mono text-[#E1252A]">{overview.total_credits_circulating}</div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
